/**
 * useKgNodeSelection
 *
 * Owns all click-interaction state for the canvas:
 *
 *   Selection (any node type):
 *     - Click a node  → highlight it + its direct parents/children (1-hop).
 *       All other nodes and edges fade to near-invisible.
 *     - Click same node again OR click the background → deselect everything.
 *     - WHY 1-hop only: the pipeline is a fully-connected DAG. Full BFS from
 *       any mid-tier node reaches all 92 nodes within a few hops, which defeats
 *       the purpose of highlighting. 1-hop is precise and actionable.
 *
 *   Expansion (table nodes only):
 *     - Click a table → expand/collapse its field list (smooth CSS transition).
 *     - Multiple tables can be expanded at once (independent toggle).
 *     - Expanded tables get zIndex 1000 so they float above other nodes.
 *     - Clicking the background collapses expanded tables back to the
 *       default compact card (3 rows + "+ N more").
 */

import { useCallback, useMemo, useState } from "react";
import type { NodeMouseHandler } from "@xyflow/react";
import { resolveVisibleCanvasNodeId } from "./resolveVisibleCanvasNodeId";
import type { KgFlowEdge, KgFlowNode } from "../types";

// ─── 1-hop neighbor lookup ────────────────────────────────────────────────────

const getDirectNeighbors = (
  seedId: string,
  allEdges: KgFlowEdge[],
): Set<string> => {
  const neighbors = new Set<string>([seedId]);
  for (const e of allEdges) {
    if (e.source === seedId) neighbors.add(e.target); // direct children
    if (e.target === seedId) neighbors.add(e.source); // direct parents
  }
  return neighbors;
};

const EDGE_OPACITY_TRANSITION = "opacity 0.25s ease";

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseKgNodeSelectionOptions {
  /** Filter-bar output — only nodes currently shown. */
  visibleNodes: KgFlowNode[];
  /** Filter-bar output — only edges currently shown. */
  visibleEdges: KgFlowEdge[];
  /** All edges (pre-filter) — used for neighbor lookup. */
  allEdges: KgFlowEdge[];
}

export interface UseKgNodeSelectionResult {
  /** Nodes with opacity / zIndex / expanded injected. Pass directly to <ReactFlow>. */
  interactiveNodes: KgFlowNode[];
  /** Edges with animated / opacity / label-opacity injected. Pass to <ReactFlow>. */
  interactiveEdges: KgFlowEdge[];
  /** Wire to ReactFlow onNodeClick. */
  handleNodeClick: NodeMouseHandler;
  /** Wire to ReactFlow onPaneClick. */
  handlePaneClick: () => void;
  /** Currently selected node id (null when nothing is selected). */
  selectedNodeId: string | null;
  /** Imperative clear — used by the inspector panel's close button. */
  clearSelection: () => void;
  /**
   * Select a node from the inspector (no toggle-off).
   * Returns the resolved canvas node id, or null if not visible.
   */
  focusNode: (targetId: string) => string | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useKgNodeSelection = ({
  visibleNodes,
  visibleEdges,
  allEdges,
}: UseKgNodeSelectionOptions): UseKgNodeSelectionResult => {
  // Which node is currently selected (null = nothing selected).
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Set of table node IDs that are in their expanded (all-fields) state.
  const [expandedTableIds, setExpandedTableIds] = useState<Set<string>>(
    new Set(),
  );

  // Direct neighbors of the selected node — null when nothing is selected.
  const connectedIds = useMemo(
    () =>
      selectedNodeId ? getDirectNeighbors(selectedNodeId, allEdges) : null,
    [selectedNodeId, allEdges],
  );

  // ── Interactive nodes: inject opacity, zIndex, and expanded flag ────────────
  const interactiveNodes = useMemo<KgFlowNode[]>(() => {
    return visibleNodes.map((node) => {
      const isExpanded = node.type === "table" && expandedTableIds.has(node.id);
      const isDimmed = connectedIds !== null && !connectedIds.has(node.id);
      const isOnPath = connectedIds !== null && connectedIds.has(node.id);

      return {
        ...node,
        selected: selectedNodeId !== null && node.id === selectedNodeId,
        zIndex: isExpanded ? 1000 : isOnPath ? 2 : 0,
        style: {
          ...node.style,
          opacity: isDimmed ? 0.28 : 1,
          transition: "opacity 0.25s ease",
        },
        data: {
          ...node.data,
          expanded: isExpanded,
        },
      };
    });
  }, [visibleNodes, connectedIds, expandedTableIds, selectedNodeId]);

  // ── Interactive edges: inject animation, stroke, and label opacity ──────────
  const interactiveEdges = useMemo<KgFlowEdge[]>(() => {
    if (!connectedIds) {
      return visibleEdges.map((edge) => ({
        ...edge,
        style: {
          ...edge.style,
          transition: EDGE_OPACITY_TRANSITION,
        },
      }));
    }

    return visibleEdges.map((edge) => {
      const isOnPath =
        connectedIds.has(edge.source) && connectedIds.has(edge.target);

      return {
        ...edge,
        animated: isOnPath,
        style: {
          ...edge.style,
          opacity: isOnPath ? 1 : 0.06,
          strokeWidth: isOnPath ? 2.5 : 1.5,
          transition: EDGE_OPACITY_TRANSITION,
        },
        // ReactFlow renders the label as a separate SVG text node — it is NOT
        // affected by style.opacity, so it must be dimmed independently.
        labelStyle: {
          ...(edge.labelStyle as React.CSSProperties | undefined),
          opacity: isOnPath ? 1 : 0.06,
        },
        labelBgStyle: {
          ...(edge.labelBgStyle as React.CSSProperties | undefined),
          fillOpacity: isOnPath ? 0.85 : 0,
        },
      };
    });
  }, [visibleEdges, connectedIds]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleNodeClick = useCallback<NodeMouseHandler>((_, node) => {
    // Toggle selection — click same node again to deselect.
    setSelectedNodeId((prev) => (prev === node.id ? null : node.id));
    // Table expansion is independent — each table toggles its own open/close.
    if (node.type === "table") {
      setExpandedTableIds((prev) => {
        const next = new Set(prev);
        if (next.has(node.id)) next.delete(node.id);
        else next.add(node.id);
        return next;
      });
    }
  }, []);

  // Pane click clears selection AND collapses expanded tables back to the
  // compact card — otherwise tables stay stuck open until re-clicked.
  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setExpandedTableIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  // Exposed for the inspector panel close button.
  const clearSelection = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const focusNode = useCallback(
    (targetId: string): string | null => {
      const resolved = resolveVisibleCanvasNodeId(targetId, visibleNodes);
      if (!resolved) return null;

      setSelectedNodeId(resolved);

      const node = visibleNodes.find((n) => n.id === resolved);
      if (node?.type === "table") {
        setExpandedTableIds((prev) => {
          const next = new Set(prev);
          next.add(resolved);
          return next;
        });
      }

      return resolved;
    },
    [visibleNodes],
  );

  return {
    interactiveNodes,
    interactiveEdges,
    handleNodeClick,
    handlePaneClick,
    selectedNodeId,
    clearSelection,
    focusNode,
  };
};
